/**
 * Service Worker Registration
 * Registers and manages the service worker lifecycle
 */

import { OFFLINE_ENABLED } from './offline/entities';

/**
 * The SW script URL to register. The SW is ALWAYS registered in prod so the
 * pre-offline terminology cache keeps working, but its NEW app-shell/static
 * caching only runs in offline mode. We signal that to the static SW file via a
 * `?offline=1` query (the SW reads it from `self.location`). With the flag OFF
 * (prod default) the bare URL registers a terminology-only SW — byte-identical
 * behavior to the pre-offline prod SW, i.e. ZERO blast radius on the merge. The
 * query is part of the SW's identity, so toggling the flag re-installs cleanly.
 */
export function buildServiceWorkerUrl(offlineEnabled: boolean): string {
  const base = `${process.env.PUBLIC_URL || ''}/service-worker.js`;
  return offlineEnabled ? `${base}?offline=1` : base;
}

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  window.location.hostname === '[::1]' ||
  window.location.hostname.match(
    /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
  )
);

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

export function register(config?: Config) {
  if ('serviceWorker' in navigator) {
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL || '', window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on.
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = buildServiceWorkerUrl(OFFLINE_ENABLED);

      if (isLocalhost) {
        // This is running on localhost. Let's check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config);

        // Add some additional logging to localhost
        navigator.serviceWorker.ready.then(() => {
          console.log(
            'This web app is being served cache-first by a service worker.'
          );
        });
      } else {
        // Is not localhost. Just register service worker
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl: string, config?: Config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // At this point, the updated precached content has been fetched,
              // but the previous service worker will still serve the older
              // content until all client tabs are closed.
              console.log(
                'New content is available and will be used when all tabs for this page are closed.'
              );

              // Execute callback
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // At this point, everything has been precached.
              console.log('Content is cached for offline use.');

              // Execute callback
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('No internet connection found. App is running in offline mode.');
    });
}

/**
 * B9: ask the browser to fire a one-off Background Sync ('stabletrack-drain')
 * when connectivity returns — so a backgrounded/throttled PWA still gets woken
 * to drain its offline queue (the SW posts DRAIN_QUEUE to open clients). Pure
 * best-effort no-op where Background Sync is unsupported. (A truly CLOSED PWA
 * needs backend Web Push — deferred.)
 */
export async function requestBackgroundDrainSync(): Promise<void> {
  if (!('serviceWorker' in navigator) || typeof window === 'undefined' || !('SyncManager' in window)) {
    return;
  }
  try {
    const reg: any = await navigator.serviceWorker.ready;
    await reg.sync?.register('stabletrack-drain');
  } catch {
    /* best-effort — unsupported or denied */
  }
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}
